<script setup lang="ts">
import { computed, toRefs } from "vue";

import { useBackendSearch } from "@/components/search/useBackendSearch";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import ListGroupHeader from "@/components/ui/ComplexList/ListGroupHeader.vue";
import ListHeaderContent from "@/components/ui/ComplexList/ListHeaderContent.vue";
import { isOrigin } from "@/model/search";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useIssueSelectorStore } from "@/store/issueSelector";
import { useTeamStore } from "@/store/team";

import IssueListItem from "./IssueListItem.vue";
import LoadingState from "./internal/LoadingState.vue";
import SectionInfoTooltip from "./internal/SectionInfoTooltip.vue";
import { issueSelectorCardFromSearchResult } from "./utils";

const store = useIssueSelectorStore();

/**
 * Search query: returns ART-level items that are assigned to the current team
 * (later, we filter out any items that are already mirrored to this team board)
 */
const searchQuery = computed(() => ({
  teams: [useTeamStore().current],
  types: store.backlogItemTypes,
  boards: [useBoardsStore().boardByType("backlog")],
}));

const { backendResults, backendSearching } = toRefs(useBackendSearch(searchQuery));

/**
 * Only show backlog items that aren't already mirrored to this board
 */
const backlogItemsNotOnBoard = computed(() => {
  // If no types can be mirrored to team boards, return empty list
  if (store.backlogItemTypes.length === 0) {
    return [];
  }

  const currentBoardCards = useBoardStore().currentBoard().cards;
  const originIdsOfCurrentBoardCards = new Set(
    Object.values(currentBoardCards).map((card) => card.data.groupId),
  );

  return backendResults.value
    .filter(
      (result) =>
        isOrigin(result) && // Only show origin items
        !originIdsOfCurrentBoardCards.has(result.id) && // Exclude items already on this board
        typeof result.iterationId !== "number", // Exclude items already in an iteration
    )
    .sort((a, b) => (b.priority || -1) - (a.priority || -1)) // Highest WSJF first, items with unspecified wsjf last
    .map(issueSelectorCardFromSearchResult);
});

const hasBacklogItems = computed(() => backlogItemsNotOnBoard.value.length > 0);
const isMirroringDisabled = computed(() => store.backlogItemTypes.length === 0);
</script>

<template>
  <ComplexList class="backlog-item-list">
    <template #header>
      <ListGroupHeader>
        <ListHeaderContent
          icon-before="board/planning"
          :text="$t('issueSelector.backlog.subtitle')"
        />
        <template #after>
          <SectionInfoTooltip
            :description="$t('issueSelector.backlog.tooltip.description')"
            :hint="isMirroringDisabled ? $t('issueSelector.backlog.tooltip.hint') : undefined"
          />
        </template>
      </ListGroupHeader>
    </template>

    <LoadingState v-if="backendSearching" />

    <ListEmpty v-else-if="!hasBacklogItems" icon="board/planning" tabindex="-1" aria-live="polite">
      <p>{{ $t("issueSelector.backlog.empty") }}</p>
    </ListEmpty>

    <IssueListItem v-for="item in backlogItemsNotOnBoard" :key="item.id" :issue="item" />
  </ComplexList>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

.backlog-item-list {
  height: unset;
  min-height: 150px;
  max-height: 33%;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
}

.loading-message,
.empty-message {
  @include typography.medium("regular");

  color: colors.$text-subtle;
  padding: 16px 12px;
  text-align: center;
}
</style>
